import { z } from 'zod';

export const workflowDtoSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  templateType: z.string(),
  type: z.string().optional().default('KYC'),
  structure: z.string().optional().default('Simple'),
  status: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  // Shared public-link slug (/wf/{slug}). Default '' for resilience on older APIs.
  publicSlug: z.string().default(''),
  // Enabled step types, ordered by position — drives the list page's Price
  // and Features columns. Default [] for resilience on older API responses.
  stepTypes: z.array(z.string()).optional().default([]),
});

export type WorkflowDto = z.infer<typeof workflowDtoSchema>;

export const pagedWorkflowsSchema = z.object({
  items: z.array(workflowDtoSchema),
  totalCount: z.number(),
  page: z.number(),
  pageSize: z.number(),
  totalPages: z.number(),
});

export const createWorkflowSchema = z.object({
  name: z.string().min(1, 'Name is required').max(200, 'Name is too long'),
  templateType: z.string().min(1, 'Please select a template type'),
  type: z.enum(['KYC', 'KYB', 'Custom']).default('KYC'),
  structure: z.enum(['Simple', 'Graph']).default('Simple'),
});

export type CreateWorkflowInput = z.infer<typeof createWorkflowSchema>;

export const TEMPLATE_TYPE_LABELS: Record<string, string> = {
  Blank: 'Blank',
  SebiKycOnboarding: 'SEBI KYC Onboarding',
  Custom: 'Custom',
};

export const WORKFLOW_TEMPLATE_OPTIONS = [
  { value: 'SebiKycOnboarding', label: 'SEBI KYC Onboarding' },
  { value: 'Custom', label: 'Custom' },
];

export const WORKFLOW_TYPE_OPTIONS = [
  { value: 'KYC', label: 'KYC' },
  { value: 'KYB', label: 'KYB' },
  { value: 'Custom', label: 'Custom' },
];

export const WORKFLOW_STRUCTURE_OPTIONS = [
  { value: 'Simple', label: 'Simple (list-based)' },
  { value: 'Graph', label: 'Graph (canvas)' },
];

// Step child DTOs
export const apiInputMappingDtoSchema = z.object({
  id: z.string().uuid(),
  apiParam: z.string(),
  sourceType: z.string(),
  sourceKey: z.string(),
});

export const stepApiBindingDtoSchema = z.object({
  id: z.string().uuid(),
  catalogueEntryId: z.string().uuid(),
  isEnabled: z.boolean(),
  priority: z.number().int(),
  inputMappings: z.array(apiInputMappingDtoSchema),
});

export const stepRiskRuleDtoSchema = z.object({
  id: z.string().uuid(),
  ruleKey: z.string(),
  isEnabled: z.boolean(),
  action: z.string(),
  config: z.string().nullable(),
});

export const stepConditionDtoSchema = z.object({
  id: z.string().uuid(),
  sortOrder: z.number().int(),
  ifField: z.string(),
  operator: z.string(),
  ifValue: z.string(),
  thenAction: z.string(),
});

export const stepReturnFieldDtoSchema = z.object({
  id: z.string().uuid(),
  fieldKey: z.string(),
  fieldGroup: z.string(),
  displayLabel: z.string(),
  isIncluded: z.boolean(),
  isDefaultIncluded: z.boolean(),
});

export const workflowStepDtoSchema = z.object({
  id: z.string().uuid(),
  stepType: z.string(),
  displayName: z.string(),
  position: z.number().int(),
  isMandatory: z.boolean(),
  isEnabled: z.boolean(),
  settings: z.string().nullable(),
  apiBindings: z.array(stepApiBindingDtoSchema),
  riskRules: z.array(stepRiskRuleDtoSchema),
  conditions: z.array(stepConditionDtoSchema),
  returnFields: z.array(stepReturnFieldDtoSchema),
});

export type WorkflowStepDto = z.infer<typeof workflowStepDtoSchema>;
export type StepApiBindingDto = z.infer<typeof stepApiBindingDtoSchema>;
export type StepRiskRuleDto = z.infer<typeof stepRiskRuleDtoSchema>;
export type StepConditionDto = z.infer<typeof stepConditionDtoSchema>;
export type StepReturnFieldDto = z.infer<typeof stepReturnFieldDtoSchema>;

export const workflowDetailDtoSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  templateType: z.string(),
  type: z.string().optional().default('KYC'),
  structure: z.string().optional().default('Simple'),
  language: z.string().optional().default('en'),
  status: z.string(),
  schemaVersion: z.number().int(),
  canvasJson: z.string().nullable(),
  settingsJson: z.string().nullable().optional(),
  steps: z.array(workflowStepDtoSchema).optional().default([]),
  createdAt: z.string(),
  updatedAt: z.string(),
  // Shared public-link slug (/wf/{slug}). Default '' for resilience on older APIs.
  publicSlug: z.string().default(''),
});

export type WorkflowDetailDto = z.infer<typeof workflowDetailDtoSchema>;

export const saveWorkflowCanvasInputSchema = z.object({
  canvasJson: z.string(),
  schemaVersion: z.number().int().positive(),
});

export type SaveWorkflowCanvasInput = z.infer<typeof saveWorkflowCanvasInputSchema>;

// API Catalogue
export const apiCatalogueEntrySchema = z.object({
  id: z.string().uuid(),
  // Console top-level category (e.g. "KYC & Identity Verification"). Backend defaults
  // to "" for legacy rows; treat empty as ungrouped on the client.
  category: z.string().default(''),
  tag: z.string(),
  httpMethod: z.string(),
  path: z.string(),
  operationId: z.string(),
  summary: z.string(),
  description: z.string().nullable(),
  consoleApiType: z.number().int().default(0),
  isAvailable: z.boolean().default(true),
  costPerCall: z.number().nullable(),
  freeMonthlyQuota: z.number().int().nullable(),
  docsUrl: z.string().nullable(),
  sandboxUrl: z.string().nullable(),
  isActive: z.boolean(),
});

export type ApiCatalogueEntry = z.infer<typeof apiCatalogueEntrySchema>;

// Save input types (for the PUT /{workflowId} endpoint)
export type SaveWorkflowInput = {
  name: string;
  description: string | null;
  language: string;
  /** Workflow-level Settings drawer config (JSON string). Null when unset. */
  settingsJson: string | null;
  steps: StepSaveInput[];
};

export type StepSaveInput = {
  id: string | null;
  stepType: string;
  displayName: string;
  position: number;
  isMandatory: boolean;
  isEnabled: boolean;
  settings: string | null;
  apiBindings: ApiBindingSaveInput[];
  riskRules: RiskRuleSaveInput[];
  conditions: ConditionSaveInput[];
  returnFields: ReturnFieldSaveInput[];
};

export type ApiBindingSaveInput = {
  id: string | null;
  catalogueEntryId: string;
  isEnabled: boolean;
  priority: number;
  inputMappings: InputMappingSaveInput[];
};

export type InputMappingSaveInput = {
  id: string | null;
  apiParam: string;
  sourceType: string;
  sourceKey: string;
};

export type RiskRuleSaveInput = {
  id: string | null;
  ruleKey: string;
  isEnabled: boolean;
  action: string;
  config: string | null;
};

export type ConditionSaveInput = {
  id: string | null;
  sortOrder: number;
  ifField: string;
  operator: string;
  ifValue: string;
  thenAction: string;
};

export type ReturnFieldSaveInput = {
  id: string | null;
  fieldKey: string;
  fieldGroup: string;
  displayLabel: string;
  isIncluded: boolean;
  isDefaultIncluded: boolean;
};
